import React, { useEffect, useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import { Avatar, Box, Button, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { PersonOutline, LocalShippingOutlined, Visibility, InsertDriveFileOutlined } from '@mui/icons-material';
import { formatCurrency } from 'utils';
import { getAssetUrl } from 'utils/getAssetUrl';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { CONTACT_AVATAR_COLORS, DEAL_BUILDER_SUMMARY_ITEMS, STEP_CONFIG } from '../../constants';
import { getContactInitials, mapQuoteCalcToSummary } from '../../lib';
import { useDealBuilderStepNavigation } from '../../hooks';
import { useDealBuilderPreviewAndSubmit } from '../../hooks/useDealBuilderPreviewAndSubmit';
import { useSelectedContactsSummary } from '../../hooks';
import { useValidatedGoToStep } from '../../hooks/useValidateAndAdvance';
import { useDealBuilderContext } from '../../context/DealBuilderContext';
import { DealBuilderStep } from '../../types';
import { DealBuilderStepper } from './DealBuilderStepper';

export const BuildAQuoteCard = () => {
  const validatedGoToStep = useValidatedGoToStep();
  const { mainContact, additionalCount, avatarIndex } = useSelectedContactsSummary();
  const { viewStep, progressStep, advanceProgressTo } = useDealBuilderStepNavigation();
  const {
    equipment: { selectedEquipmentItem },
    pricing: { priceCalc, isQuoteCalcLoading },
    activeEntity,
    startNewQuote,
  } = useDealBuilderContext();
  const { isPreviewLoading, handlePreview } = useDealBuilderPreviewAndSubmit();

  const summary = mapQuoteCalcToSummary(priceCalc);

  const completedSteps = useMemo(() => {
    const set = new Set<DealBuilderStep>();

    const quoteEquipment = activeEntity?.quote?.equipment?.[0];

    const hasContacts = !!mainContact;
    const hasEquipment = !!selectedEquipmentItem;
    const hasQuote = !!quoteEquipment && selectedEquipmentItem?.equipment?.id === quoteEquipment?.equipmentId;
    const hasFinanceAndMenu = !!activeEntity?.proposal?.settings?.configuration?.pricing; /* .formData */
    if (hasContacts) set.add(DealBuilderStep.Contacts);
    if (hasContacts && hasEquipment) set.add(DealBuilderStep.Equipment);
    if (hasContacts && hasEquipment && hasQuote) set.add(DealBuilderStep.Pricing);
    if (hasContacts && hasEquipment && hasQuote && hasFinanceAndMenu) set.add(DealBuilderStep.FinanceMenu);
    return set;
  }, [mainContact, selectedEquipmentItem, activeEntity?.quote, activeEntity?.proposal?.settings]);

  // Sync URL progressStep with steps completed from backend data on load
  useEffect(() => {
    const highest = [...STEP_CONFIG].reverse().find(({ step }) => completedSteps.has(step))?.step;
    if (highest) advanceProgressTo(highest);
  }, [completedSteps, advanceProgressTo]);

  const handlePreviewClick = async () => {
    const linkToProposalPage = await handlePreview();
    if (linkToProposalPage) {
      window.open(linkToProposalPage, '_blank');
    }
  };

  return (
    <CardContainer
      title="Build a Quote"
      subTitle="Create a quote for this opportunity"
      style={{ height: 'auto', overflow: 'visible' }}
      actionBtn={
        activeEntity ? (
          <Stack direction="row" gap={1}>
            <Button size="small" startIcon={<Visibility />} onClick={handlePreviewClick} loading={isPreviewLoading}>
              Preview
            </Button>
            <Button size="small" startIcon={<InsertDriveFileOutlined />} onClick={() => startNewQuote()}>
              Start New Quote
            </Button>
          </Stack>
        ) : undefined
      }
    >
      <DealBuilderStepper
        viewStep={viewStep}
        progressStep={progressStep}
        completedSteps={completedSteps}
        onStepClick={validatedGoToStep}
      />

      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}
      >
        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem />}
          sx={{ flex: '0 0 50%', minWidth: 0 }}
        >
          <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1, flex: 1, minWidth: 0 }}>
            {mainContact ? (
              <>
                <Avatar
                  sx={{
                    bgcolor: CONTACT_AVATAR_COLORS[avatarIndex % CONTACT_AVATAR_COLORS.length],
                    color: 'black',
                  }}
                >
                  <Typography variant="subtitle2">{getContactInitials(mainContact.fullName)}</Typography>
                </Avatar>
                <Stack minWidth={0}>
                  <Typography variant="body2" noWrap>
                    {mainContact.fullName}
                  </Typography>
                  {mainContact.title && (
                    <Typography variant="caption" color="text.secondary" fontWeight={500} noWrap>
                      {mainContact.title}
                    </Typography>
                  )}
                  {additionalCount > 0 && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      +{additionalCount} additional recipient{additionalCount > 1 ? 's' : ''}
                    </Typography>
                  )}
                </Stack>
              </>
            ) : (
              <>
                <Avatar>
                  <PersonOutline />
                </Avatar>
                <Typography variant="body2" color="text.secondary" noWrap>
                  No contact selected yet
                </Typography>
              </>
            )}
          </Stack>

          <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1, flex: 1, minWidth: 0 }}>
            {selectedEquipmentItem ? (
              <>
                <Avatar
                  variant="rounded"
                  src={
                    selectedEquipmentItem.equipment.assets?.[0]?.source
                      ? getAssetUrl(selectedEquipmentItem.equipment.assets[0].source)
                      : undefined
                  }
                >
                  <LocalShippingOutlined />
                </Avatar>
                <Stack minWidth={0}>
                  <Typography variant="body2" noWrap>
                    {selectedEquipmentItem.equipment.details.name}
                  </Typography>
                </Stack>
              </>
            ) : (
              <>
                <Avatar variant="rounded">
                  <LocalShippingOutlined />
                </Avatar>
                <Typography variant="body2" color="text.secondary" noWrap>
                  No equipment selected yet
                </Typography>
              </>
            )}
          </Stack>
        </Stack>

        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem />}
          sx={{ flex: '0 0 50%', minWidth: 0 }}
        >
          {DEAL_BUILDER_SUMMARY_ITEMS.map(({ key, label, color }) => (
            <Box
              key={key}
              sx={{
                px: 2,
                py: 1,
                flex: 1,
                minWidth: 0,
                bgcolor: theme => (color === 'default' ? 'transparent' : alpha(theme.palette[color].main, 0.08)),
              }}
            >
              <Typography
                noWrap
                fontWeight={400}
                variant="subtitle2"
                sx={{ textTransform: 'none' }}
                color={color === 'default' ? 'text.secondary' : `${color}.dark`}
              >
                {label}
              </Typography>

              {isQuoteCalcLoading ? (
                <Skeleton variant="text" width="80%" />
              ) : (
                <Typography variant="subtitle1" fontWeight={500} noWrap>
                  {formatCurrency(summary[key], true)}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </Stack>
    </CardContainer>
  );
};
